// `/admin/retire` — this week's cold accounts, proposed for retirement (plans/retire-cold-accounts.md).
//
// Admin only, like the rest of `/admin`: the server's `require_admin_view` decides, and the page
// redirects anyone it is not offered to. Every row starts ticked; untick what should stay, then
// "Retire N" retires the rest now. The server re-checks each account first and skips anything that
// changed since Monday (a reply, a meeting, a pin, a new owner). The same approval is the
// "Retire all N" button on the admin's Monday brief in Slack; whichever comes first wins, and the
// other then says it was already approved. While SCOUT_RETIRE is off the page shows the proposal
// and the button answers that retire is off.

import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccessDeniedError, applyRetireProposal, fetchMe, fetchRetireProposal, panelMessage } from '../api/client';
import type { RetireApplyPayload, RetireCandidate, RetireItemResult, RetirePayload } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { AdminSection } from '../admin/AdminSection';
import { PageChrome } from '../components/PageChrome';
import { accountsWord, answerLine, groupByOwner, retireButtonLabel } from '../lib/retire';

const PROPOSAL_KEY = ['admin-retire'];

function CandidateRow({ row, ticked, editable, onToggle }: {
  row: RetireCandidate;
  ticked: boolean;
  editable: boolean;
  onToggle: (accountId: string) => void;
}) {
  return (
    <tr>
      {editable ? (
        <td>
          <input type="checkbox" aria-label={`Retire ${row.name}`} checked={ticked}
            onChange={() => onToggle(row.account_id)} />
        </td>
      ) : null}
      <td><a href={`/execution?account=${encodeURIComponent(row.account_id)}`}>{row.name || row.account_id}</a></td>
      <td>{row.domain}</td>
      <td>{row.days_cold}d</td>
      <td>{row.last_two_way_on ?? 'never'}</td>
      <td>{row.last_signal_on ?? 'none'}</td>
      <td className="retirereason">{row.reason}</td>
    </tr>
  );
}

function ResultTable({ items }: { items: RetireItemResult[] }) {
  if (!items.length) return null;
  return (
    <table className="retiretable">
      <thead><tr><th>Account</th><th>Owner</th><th>Outcome</th><th>Why</th></tr></thead>
      <tbody>
        {items.map(item => (
          <tr key={item.account_id} className={'retire' + item.outcome}>
            <td>{item.name || item.account_id}</td>
            <td>{item.owner_email}</td>
            <td><b>{item.outcome.replace('_', ' ')}</b></td>
            <td>{item.detail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RetireView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [resultLine, setResultLine] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => fetchMe(getToken) });
  const viewer = meQuery.data?.viewer;
  const allowed = Boolean(viewer?.sees_admin_view);
  const proposalQuery = useQuery({ queryKey: PROPOSAL_KEY, queryFn: () => fetchRetireProposal(getToken), enabled: allowed });

  const apply = useMutation({
    mutationFn: (input: { proposalKey: string; accountIds: string[]; fingerprint: string | null }) =>
      applyRetireProposal(getToken, input.proposalKey, input.accountIds, input.fingerprint),
    onSuccess: (payload: RetireApplyPayload) => {
      const next: RetirePayload = { unavailable: null, retire_on: payload.retire_on, proposal: payload.proposal };
      queryClient.setQueryData(PROPOSAL_KEY, next);
      setResultLine(answerLine(payload.answer));
      setActionError(null);
      setUnticked(new Set());
    },
    onError: (error: unknown) => setActionError(panelMessage(error, 'Could not retire those accounts')),
  });

  const proposal = proposalQuery.data?.proposal ?? null;
  const retireOn = Boolean(proposalQuery.data?.retire_on);
  const candidates = proposal?.candidates ?? [];
  const groups = useMemo(() => groupByOwner(candidates), [candidates]);

  if (meQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={meQuery.error.message} email={email} onSignOut={signOut} />;
  }
  if (meQuery.isPending) return <div className="loading">Signing you in…</div>;
  // Hide, do not stub: anyone without the admin console lands on their own board.
  if (viewer && !viewer.sees_admin_view) {
    return <Navigate to={viewer.default_view === 'execution' ? '/execution' : '/activity'} replace />;
  }

  const editable = proposal?.status === 'proposed';
  const tickedIds = candidates.map(row => row.account_id).filter(accountId => !unticked.has(accountId));

  function toggle(accountId: string) {
    setUnticked(current => {
      const next = new Set(current);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }

  function retire() {
    if (!proposal || !tickedIds.length) return;
    const question = `Retire ${accountsWord(tickedIds.length)} now? Each moves to the bank as retired after a last check.`;
    if (retireOn && !window.confirm(question)) return;
    apply.mutate({ proposalKey: proposal.proposal_key, accountIds: tickedIds, fingerprint: proposal.fingerprint });
  }

  const storedItems = proposal?.result?.items ?? [];
  return (
    <PageChrome freshness={null} viewer={viewer}>
      <p className="sub">
        Accounts with no two-way contact and no real signal for {proposal?.cold_days ?? 28} days. Retiring one moves it to
        the bank as <code>retired</code>: off every board, out of nightly enrichment, scoring, top-up, the auto lane
        and re-research. Scout still watches it (door knocks, funding, usage, a monthly sweep) and wakes it on a real signal.
      </p>
      {!retireOn ? (
        <p className="sendmode dry">Retire is off (SCOUT_RETIRE). Set SCOUT_RETIRE=on, after adding <code>retired</code>
          to the Scout_State__c picklist, to approve; until then the proposal is shown and nothing is retired.</p>
      ) : null}
      {proposalQuery.error ? <div className="err"><b>Could not load the proposal.</b><br />{(proposalQuery.error as Error).message}</div> : null}
      {actionError ? <div className="err">{actionError}</div> : null}
      {resultLine ? <p className="sendresults" role="status">{resultLine}</p> : null}

      <AdminSection
        title={proposal ? `Week of ${proposal.proposal_key}` : 'This week'}
        count={proposal ? `${accountsWord(candidates.length)} ${editable ? 'ready to retire' : 'proposed'}` : null}
        loading={proposalQuery.isPending}
        unavailable={proposalQuery.data?.unavailable}
      >
        {!proposal ? <p className="adminnote">No proposal yet. It is built on Monday mornings by the nightly run.</p> : null}
        {proposal && !editable ? (
          <p className="adminnote">Approved by {proposal.applied_by ?? 'an admin'}
            {proposal.applied_at ? ` on ${proposal.applied_at.slice(0, 10)}` : ''}.</p>
        ) : null}
        {editable && candidates.length ? (
          <div className="sendbulk">
            <button className="btn primary" type="button" disabled={apply.isPending || tickedIds.length === 0} onClick={retire}>
              {retireButtonLabel(tickedIds.length, retireOn)}
            </button>
            {unticked.size ? <span className="sendquiet">{unticked.size} kept</span> : null}
          </div>
        ) : null}
        {editable ? groups.map(group => (
          <div key={group.key} className="retiregroup">
            <h3>{group.label} · {accountsWord(group.rows.length)}</h3>
            <table className="retiretable">
              <thead>
                <tr><th>Retire</th><th>Account</th><th>Domain</th><th>Cold</th><th>Last two-way</th><th>Last signal</th><th>Why</th></tr>
              </thead>
              <tbody>
                {group.rows.map(row => (
                  <CandidateRow key={row.account_id} row={row} ticked={!unticked.has(row.account_id)} editable
                    onToggle={toggle} />
                ))}
              </tbody>
            </table>
          </div>
        )) : <ResultTable items={storedItems} />}
      </AdminSection>
    </PageChrome>
  );
}
